import {
  GlobalSearchControlType,
  getFilterDefinitionByKey,
  schemaHasGlobalControl,
  type FilterDefinition,
  type StructuredSearchSchema,
  type ValueSuggestionOption,
  type ValueSuggestionsHookArgs,
  type ValueSuggestionsHookResult,
} from "./structured-search-schema";
import { usesDropdownValueStep } from "./suggestion-config";
import type { FilterValue, OperatorDefinition } from "./types";
import {
  getNumericInputKindForType,
  usesStaticEnumSuggestions,
} from "./filter-value-type-behavior";
import { coerceFilterValue, type ValueCoercionResult } from "./value-coercion";
import { formatFilterValueByType } from "./value-formatter";

import { calculateStringSimilarity, normalizeForFuzzySearch } from "@/utils/fuzzy-search";

const FIELD_FUZZY_MATCH_THRESHOLD = 0.6;
const OPERATOR_FUZZY_MATCH_THRESHOLD = 0.7;
const KEY_CONTAINS_OPERATOR = "key_contains";
const KEY_NOT_CONTAINS_OPERATOR = "key_not_contains";

export type { ValueSuggestionOption, ValueSuggestionsHookArgs, ValueSuggestionsHookResult };

export interface StaticValueOption {
  value: string;
  count?: number;
}

export type NumericInputKind = import("./filter-value-type-behavior").NumericInputKind;

export interface OperatorDisplayArgs {
  operator?: string;
  field?: string;
  nestedKey?: string;
}

export interface CompletionCoercionArgs {
  field: string;
  operator: string;
  rawInput: string;
  value: FilterValue | undefined;
}

export const useEmptyValueSuggestions = (): ValueSuggestionsHookResult => ({
  options: null,
  isLoading: false,
});

const getOperatorSearchScore = (operator: OperatorDefinition, normalizedQuery: string): number => {
  const normalizedCandidates = [
    normalizeForFuzzySearch(operator.operator),
    normalizeForFuzzySearch(operator.display),
  ];

  if (normalizedCandidates.some(candidate => candidate === normalizedQuery)) {
    return 1;
  }

  if (normalizedCandidates.some(candidate => candidate.includes(normalizedQuery))) {
    return 0.9;
  }

  const similarity = Math.max(
    ...normalizedCandidates.map(candidate => calculateStringSimilarity(candidate, normalizedQuery)),
  );

  return similarity >= OPERATOR_FUZZY_MATCH_THRESHOLD ? similarity : 0;
};

const matchOperatorList = (
  operators: OperatorDefinition[],
  query: string,
): OperatorDefinition[] => {
  const normalized = normalizeForFuzzySearch(query.trim());

  if (!normalized) {
    return operators;
  }

  const matches = operators
    .map((operator, index) => ({
      operator,
      index,
      score: getOperatorSearchScore(operator, normalized),
    }))
    .filter(match => match.score > 0)
    .sort((a, b) => b.score - a.score || a.index - b.index);
  const highConfidenceMatches = matches.filter(match => match.score >= 0.9);

  return (highConfidenceMatches.length > 0 ? highConfidenceMatches : matches).map(
    match => match.operator,
  );
};

const getFieldSearchScore = (filter: FilterDefinition, normalizedQuery: string): number => {
  const normalizedCandidates = [
    normalizeForFuzzySearch(filter.key),
    normalizeForFuzzySearch(filter.label),
  ];

  if (normalizedCandidates.some(candidate => candidate === normalizedQuery)) {
    return 1;
  }

  if (
    normalizedCandidates.some(
      candidate => candidate.includes(normalizedQuery) || normalizedQuery.includes(candidate),
    )
  ) {
    return 0.9;
  }

  const similarity = Math.max(
    ...normalizedCandidates.map(candidate => calculateStringSimilarity(candidate, normalizedQuery)),
  );

  return similarity >= FIELD_FUZZY_MATCH_THRESHOLD ? similarity : 0;
};

const getAliasMap = (schema: StructuredSearchSchema): Record<string, string> => {
  const aliases: Record<string, string> = {};
  schema.filters.forEach(filter => {
    filter.aliases?.forEach(alias => {
      aliases[alias.trim().toLowerCase()] = filter.key;
    });
  });
  return aliases;
};

export const matchFilterDefinitions = (
  schema: StructuredSearchSchema,
  query: string,
): FilterDefinition[] => {
  const normalized = normalizeForFuzzySearch(query.trim());
  if (!normalized) {
    return [...schema.filters];
  }

  const aliases = getAliasMap(schema);
  const aliasField = aliases[query.trim().toLowerCase()];
  if (aliasField) {
    const definition = getFilterDefinitionByKey(schema, aliasField);
    return definition ? [definition] : [];
  }

  const groupOrder = new Map<string, number>();
  schema.filters.forEach(filter => {
    const groupKey = filter.group ?? "";
    if (!groupOrder.has(groupKey)) {
      groupOrder.set(groupKey, groupOrder.size);
    }
  });

  return schema.filters
    .map((filter, index) => ({
      filter,
      index,
      score: getFieldSearchScore(filter, normalized),
    }))
    .filter(match => match.score > 0)
    .sort(
      (a, b) =>
        (groupOrder.get(a.filter.group ?? "") ?? groupOrder.size) -
          (groupOrder.get(b.filter.group ?? "") ?? groupOrder.size) ||
        b.score - a.score ||
        a.index - b.index,
    )
    .map(match => match.filter);
};

export const getOperatorsForFilter = (
  schema: StructuredSearchSchema,
  field?: string,
): OperatorDefinition[] => getFilterDefinitionByKey(schema, field)?.operators ?? [];

export const matchOperatorsForFilter = (
  schema: StructuredSearchSchema,
  field: string | undefined,
  query: string,
): OperatorDefinition[] => matchOperatorList(getOperatorsForFilter(schema, field), query);

export const getOperatorDisplay = (
  schema: StructuredSearchSchema,
  { operator, field, nestedKey }: OperatorDisplayArgs,
): string => {
  if (!operator) {
    return "";
  }
  const opDef = getOperatorsForFilter(schema, field).find(op => op.operator === operator);
  return (opDef?.display ?? operator).replace("[key]", nestedKey ?? "key");
};

export const shouldSuppressValueDropdown = (
  schema: StructuredSearchSchema,
  field?: string,
  operator?: string,
): boolean => {
  if (operator === KEY_CONTAINS_OPERATOR || operator === KEY_NOT_CONTAINS_OPERATOR) {
    return true;
  }

  const definition = getFilterDefinitionByKey(schema, field);
  return definition ? !usesDropdownValueStep(definition) : true;
};

export const getNumericInputKind = (
  schema: StructuredSearchSchema,
  field?: string,
): NumericInputKind => {
  const definition = getFilterDefinitionByKey(schema, field);
  return definition ? getNumericInputKindForType(definition.type) : "number";
};

export const usesStaticValueSuggestions = (
  schema: StructuredSearchSchema,
  field: string | undefined,
): boolean => {
  const definition = getFilterDefinitionByKey(schema, field);
  return definition ? usesStaticEnumSuggestions(definition.type) : false;
};

export const getStaticValueOptions = (
  schema: StructuredSearchSchema,
  field: string | undefined,
  input: string,
): StaticValueOption[] | null => {
  const definition = getFilterDefinitionByKey(schema, field);
  if (!definition || !usesStaticEnumSuggestions(definition.type) || !definition.options?.length) {
    return null;
  }

  const normalized = input.trim().toLowerCase();
  return definition.options
    .filter(
      option =>
        !normalized ||
        option.value.toLowerCase().includes(normalized) ||
        option.label.toLowerCase().includes(normalized),
    )
    .map(option => ({ value: option.value }));
};

export const formatFilterValueForDisplay = (
  schema: StructuredSearchSchema,
  value: FilterValue,
  field?: string,
): string => {
  const definition = getFilterDefinitionByKey(schema, field);
  if (!definition) {
    return String(value);
  }

  return formatFilterValueByType({
    type: definition.type,
    value,
    operator: "",
    options: definition.options,
  });
};

export const coerceCompletionValue = (
  schema: StructuredSearchSchema,
  { field, operator, rawInput, value }: CompletionCoercionArgs,
): ValueCoercionResult => {
  const definition = getFilterDefinitionByKey(schema, field);
  if (!definition) {
    return null;
  }
  return coerceFilterValue({ type: definition.type, operator, rawInput, value });
};

export const hasDateRangeControl = (schema: StructuredSearchSchema): boolean =>
  schemaHasGlobalControl(schema, GlobalSearchControlType.DateRange);

export const hasTimezoneControl = (schema: StructuredSearchSchema): boolean =>
  schemaHasGlobalControl(schema, GlobalSearchControlType.Timezone);

export const hasFreeTextQueryControl = (schema: StructuredSearchSchema): boolean =>
  schemaHasGlobalControl(schema, GlobalSearchControlType.FreeTextQuery);

export const hasCutoverDateControl = (schema: StructuredSearchSchema): boolean =>
  schemaHasGlobalControl(schema, GlobalSearchControlType.CutoverDate);
